import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../utils/init-firebase';
import { DEFAULT_PREFERENCES } from '../components/CRM/tableColumnDefinitions';

// Debounce helper
const debounce = (func, wait) => {
  let timeout;
  return (...args) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
};

/**
 * Hook for managing CRM table preferences with Firestore persistence.
 * 
 * @param {string} userId - The user's ID
 * @param {object} options - Options object
 * @param {object} options.defaultPreferences - Custom default preferences (for contacts vs deals tables)
 * @param {string} options.preferencesKey - Firestore field name for preferences (default: 'crmTablePreferences')
 */
export function useCRMTablePreferences(userId, options = {}) {
  const { 
    defaultPreferences = DEFAULT_PREFERENCES,
    preferencesKey = 'crmTablePreferences'
  } = options;
  
  // Use ref to track defaults for use in callbacks without causing re-renders
  const defaultsRef = useRef(defaultPreferences);
  defaultsRef.current = defaultPreferences;
  
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(true);

  // Load preferences from Firestore
  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    const userRef = doc(db, 'users', userId);
    
    const unsubscribe = onSnapshot(
      userRef,
      (docSnap) => {
        if (docSnap.exists()) {
          const userData = docSnap.data();
          const savedPrefs = userData[preferencesKey];
          if (savedPrefs) {
            setPreferences({
              ...defaultsRef.current,
              ...savedPrefs,
            });
          } else {
            setPreferences(defaultsRef.current);
          }
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error loading CRM table preferences:', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [userId, preferencesKey]);

  // Debounced save to Firestore
  const debouncedSave = useCallback(
    debounce(async (userId, newPrefs, prefKey) => {
      if (!userId) return;

      try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          [prefKey]: {
            ...newPrefs,
            savedAt: new Date(),
          },
        });
      } catch (error) {
        console.error('Error saving CRM table preferences:', error);
      }
    }, 1000),
    []
  );

  const updatePreferences = useCallback(
    (updates) => {
      const newPrefs = { ...preferences, ...updates };
      setPreferences(newPrefs);
      
      if (userId) {
        debouncedSave(userId, newPrefs, preferencesKey);
      }
    },
    [preferences, userId, debouncedSave, preferencesKey]
  );

  const resetToDefaults = useCallback(async () => {
    const defaults = defaultsRef.current;
    setPreferences(defaults);
    
    if (userId) {
      try {
        const userRef = doc(db, 'users', userId);
        await updateDoc(userRef, {
          [preferencesKey]: {
            ...defaults,
            savedAt: new Date(),
          },
        });
      } catch (error) {
        console.error('Error resetting preferences:', error);
        throw error;
      }
    }
  }, [userId, preferencesKey]);

  return {
    preferences,
    updatePreferences,
    resetToDefaults,
    loading,
  };
}


